import { Filter } from '@solar-icons/react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Accordion } from 'react-bootstrap';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import FilterAccordionItem from '~/components/filter/FilterAccordionItem';
import { FiltersFormValues } from '~/schemas/filter.schema';
import { FilterDefinition } from '~/types/Filter';

type FiltersFormProps = {
  filters: FilterDefinition[];
  initialValues?: FiltersFormValues;
  onChange: (_filters: FiltersFormValues) => void;
  totalProductsCount?: number;
  filteredProductsCount?: number;
  loading?: boolean;
};

const buildDefaultValues = (filters: FilterDefinition[]): FiltersFormValues => {
  const defaults: FiltersFormValues = {};

  filters.forEach((filter) => {
    if ('buckets' in filter) {
      defaults[filter.key] = [];
    }
    if ('min' in filter) {
      defaults[filter.key] = [filter.min, filter.max];
    }
  });

  return defaults;
};

const FiltersSkeleton = () => (
  <>
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="filter-item" style={{ padding: '1rem 0' }}>
        <Skeleton width="60%" height={20} style={{ marginBottom: '0.75rem' }} />
        {Array.from({ length: 4 }).map((_, j) => (
          <Skeleton key={j} width="80%" height={16} style={{ marginBottom: '0.5rem' }} />
        ))}
      </div>
    ))}
  </>
);

const FiltersForm = ({
  filters,
  initialValues,
  onChange,
  totalProductsCount,
  filteredProductsCount,
  loading,
}: FiltersFormProps) => {
  const { t } = useTranslation();
  const isSyncingFromUrl = useRef(false);
  const snapshot = useMemo(
    () => ({
      ...buildDefaultValues(filters),
      ...initialValues,
    }),
    [filters, initialValues]
  );

  const methods = useForm<FiltersFormValues>({
    defaultValues: snapshot,
    shouldUnregister: false,
  });

  const { control, reset } = methods;

  useEffect(() => {
    isSyncingFromUrl.current = true;

    reset(snapshot, {
      keepDirty: false,
      keepTouched: false,
    });

    requestAnimationFrame(() => {
      isSyncingFromUrl.current = false;
    });
  }, [snapshot, reset]);

  const values = useWatch({ control });
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const debouncedOnChange = useCallback(
    (formValues: FiltersFormValues) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onChange(formValues);
      }, 150);
    },
    [onChange]
  );

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  useEffect(() => {
    if (isSyncingFromUrl.current) return;
    debouncedOnChange(values);
  }, [values, debouncedOnChange]);

  const activeKeys = useMemo(() => {
    const firstThreeKeys = filters.slice(0, 3).map((f) => f.key);
    const activeFilterKeys = filters
      .filter((filter) => {
        const value = initialValues?.[filter.key];
        if (!value) return false;

        // Bucket filter - active if array is not empty
        if (Array.isArray(value) && 'buckets' in filter) {
          return value.length > 0;
        }

        // Range filter - active if values differ from default min/max
        if (Array.isArray(value) && 'min' in filter) {
          return value[0] !== filter.min || value[1] !== filter.max;
        }

        return false;
      })
      .map((f) => f.key);

    return [...new Set([...firstThreeKeys, ...activeFilterKeys])];
  }, [filters, initialValues]);

  const isInitialLoad = loading && filters.length === 0;
  const isReloading = loading && filters.length > 0;

  return (
    <FormProvider {...methods}>
      <div className="filter-container">
        <div className="filter-header">
          <Filter weight="Outline" size={24} color="#F96D3B" />
          <strong className="fw-medium text-uppercase">{t('components.filter.title')}</strong>
          <p className="filter-header__items-shown">
            {isInitialLoad ? (
              <Skeleton width={120} />
            ) : (
              <span>
                <Trans
                  i18nKey="components.filter.items_shown"
                  values={{ count: filteredProductsCount, total: totalProductsCount }}
                  components={[<strong />]}
                />
              </span>
            )}
          </p>
        </div>
        {isInitialLoad ? (
          <FiltersSkeleton />
        ) : (
          <form role="search" className={isReloading ? 'filter-form--reloading' : ''}>
            <Accordion alwaysOpen defaultActiveKey={activeKeys}>
              {filters.map((filter) => (
                <FilterAccordionItem key={filter.key} filter={filter} />
              ))}
            </Accordion>
          </form>
        )}
      </div>
    </FormProvider>
  );
};

export default FiltersForm;
